/**
 * Extract included baggage counts and paid-bag prices for FlightOfferCard
 * (briefcase = checked, backpack = carry-on). Supports Duffel segment passengers
 * and SerpAPI / Google Flights booking_option strings.
 */

/** @param {string} amountStr */
export function formatBaggagePrice(amountStr) {
  if (!amountStr) return null;
  const trimmed = String(amountStr).trim();
  if (trimmed.includes('-')) {
    const [lo, hi] = trimmed.split('-').map((s) => s.trim());
    const loNum = parseInt(lo, 10);
    const hiNum = parseInt(hi, 10);
    if (!Number.isFinite(loNum) || !Number.isFinite(hiNum)) return null;
    return `$${loNum.toLocaleString('en-US')}–$${hiNum.toLocaleString('en-US')}`;
  }
  const n = parseInt(trimmed, 10);
  if (!Number.isFinite(n)) return null;
  return `$${n.toLocaleString('en-US')}`;
}

/** @param {number} count @param {string | null | undefined} price */
export function formatBaggageAllowanceLabel(count, price) {
  if (count > 0) return String(count);
  if (price) return price;
  return String(count ?? 0);
}

/** @param {string | null | undefined} formattedPrice @param {number} [divisor] */
export function divideFormattedBaggagePrice(formattedPrice, divisor = 2) {
  if (!formattedPrice || divisor <= 0) return formattedPrice;
  if (formattedPrice.includes('–')) {
    const [loRaw, hiRaw] = formattedPrice.replace(/\$/g, '').split('–');
    const lo = parseInt(String(loRaw).replace(/,/g, ''), 10);
    const hi = parseInt(String(hiRaw).replace(/,/g, ''), 10);
    if (!Number.isFinite(lo) || !Number.isFinite(hi)) return formattedPrice;
    return formatBaggagePrice(`${Math.round(lo / divisor)}-${Math.round(hi / divisor)}`);
  }
  const amount = parseInt(String(formattedPrice).replace(/[$,]/g, ''), 10);
  if (!Number.isFinite(amount)) return formattedPrice;
  return formatBaggagePrice(String(Math.round(amount / divisor)));
}

function applyTripLevelPerWayPricing(allowance, { priceScope, roundTrip }) {
  if (!allowance || priceScope !== 'trip' || !roundTrip) return allowance;
  return {
    ...allowance,
    checkedPrice: allowance.checkedPrice
      ? divideFormattedBaggagePrice(allowance.checkedPrice)
      : null,
    carryOnPrice: allowance.carryOnPrice
      ? divideFormattedBaggagePrice(allowance.carryOnPrice)
      : null,
  };
}

function mergeBaggageInfo(target, next) {
  if (!next) return target;
  if (next.checked != null) {
    target.checked = target.checked == null ? next.checked : Math.max(target.checked, next.checked);
  }
  if (next.carryOn != null) {
    target.carryOn = target.carryOn == null ? next.carryOn : Math.max(target.carryOn, next.carryOn);
  }
  if (next.checkedPrice && !target.checkedPrice) target.checkedPrice = next.checkedPrice;
  if (next.carryOnPrice && !target.carryOnPrice) target.carryOnPrice = next.carryOnPrice;
  return target;
}

function finalizeBaggageInfo(merged) {
  if (
    merged.checked == null &&
    merged.carryOn == null &&
    !merged.checkedPrice &&
    !merged.carryOnPrice
  ) {
    return null;
  }
  return {
    checked: merged.checked ?? 0,
    carryOn: merged.carryOn ?? 0,
    checkedPrice: merged.checkedPrice ?? null,
    carryOnPrice: merged.carryOnPrice ?? null,
    priceScope: merged.priceScope ?? null,
  };
}

/**
 * Parse a single human-readable baggage line from SerpAPI baggage_prices or extensions.
 * @returns {{ checked?: number, carryOn?: number, checkedPrice?: string, carryOnPrice?: string } | null}
 */
export function parseBaggageString(raw) {
  const lower = String(raw || '').toLowerCase().trim();
  if (!lower) return null;
  if (!/(carry[- ]?on|checked|personal item|baggage|luggage)/.test(lower)) return null;

  const result = {};

  if (/carry[- ]?on/.test(lower)) {
    if (/free carry[- ]?on|carry[- ]?on included|1 carry[- ]?on included/.test(lower)) {
      const counted = lower.match(/(\d+)\s*(?:free\s+)?carry[- ]?on/);
      result.carryOn = counted ? parseInt(counted[1], 10) : 1;
    } else {
      const priceMatch =
        lower.match(/carry[- ]?on(?: bag[s]?)?\s*:?\s*\$?\s*(\d+(?:-\d+)?)/) ||
        lower.match(/(?:1st|2nd|first|second)\s+carry[- ]?on(?: bag[s]?)?\s*:?\s*\$?\s*(\d+(?:-\d+)?)/);
      if (priceMatch) {
        result.carryOn = 0;
        result.carryOnPrice = formatBaggagePrice(priceMatch[1]);
      } else if (
        /no carry[- ]?on|not include carry[- ]?on|carry[- ]?on for a fee|carry[- ]?on not included/.test(lower)
      ) {
        result.carryOn = 0;
      }
    }
  }

  if (/checked/.test(lower)) {
    if (/free checked|checked bag.*free|checked bags.*free|free.*checked bag/.test(lower)) {
      const counted = lower.match(/(\d+)\s*(?:free\s+)?checked/);
      result.checked = counted ? parseInt(counted[1], 10) : 1;
    } else {
      const priceMatch =
        lower.match(/(?:1st|2nd|first|second|third)\s+checked bag[s]?\s*:?\s*\$?\s*(\d+(?:-\d+)?)/) ||
        lower.match(/checked bag[s]?(?:gage)?\s*:?\s*\$?\s*(\d+(?:-\d+)?)/);
      if (priceMatch) {
        result.checked = 0;
        result.checkedPrice = formatBaggagePrice(priceMatch[1]);
      } else if (
        /no checked|no free checked|checked bag for a fee|checked bags for a fee|checked baggage for a fee/.test(lower)
      ) {
        result.checked = 0;
      } else {
        const counted = lower.match(/(\d+)\s*(?:free\s+)?checked/);
        if (counted) result.checked = parseInt(counted[1], 10);
      }
    }
  }

  if (/personal item/.test(lower)) {
    const counted = lower.match(/(\d+)\s*(?:free\s+)?personal/);
    const qty = counted ? parseInt(counted[1], 10) : 1;
    if (/free personal/.test(lower) || !/for a fee/.test(lower)) {
      result.carryOn = result.carryOn == null ? qty : Math.max(result.carryOn, qty);
    }
  }

  return Object.keys(result).length > 0 ? result : null;
}

/** @param {string[]} strings */
export function parseBaggageStrings(strings) {
  if (!Array.isArray(strings) || strings.length === 0) return null;
  const merged = {};
  for (const line of strings) {
    mergeBaggageInfo(merged, parseBaggageString(line));
  }
  return finalizeBaggageInfo(merged);
}

/** @param {Array<{ segments?: Array<{ passengers?: Array<{ baggages?: Array<{ type?: string, quantity?: number }> }> }> }>} slices */
export function getBaggageAllowanceFromDuffelSlices(slices) {
  if (!Array.isArray(slices) || slices.length === 0) return null;

  let checked = 0;
  let carryOn = 0;
  let found = false;

  for (const slice of slices) {
    for (const segment of slice?.segments || []) {
      for (const passenger of segment?.passengers || []) {
        for (const bag of passenger?.baggages || []) {
          found = true;
          const qty = Number(bag?.quantity) || 0;
          if (bag?.type === 'checked') checked = Math.max(checked, qty);
          if (bag?.type === 'carry_on') carryOn = Math.max(carryOn, qty);
        }
      }
    }
  }

  return found
    ? { checked, carryOn, checkedPrice: null, carryOnPrice: null, priceScope: null }
    : null;
}

/** @param {string | null | undefined} formattedPrice — e.g. "$45" or "$99–$187" (uses lower bound) */
export function parseFormattedBaggagePriceToCents(formattedPrice) {
  if (!formattedPrice) return null;
  const firstPart = formattedPrice.includes('–') ? formattedPrice.split('–')[0] : formattedPrice;
  const amount = parseInt(String(firstPart).replace(/[$,]/g, ''), 10);
  if (!Number.isFinite(amount) || amount <= 0) return null;
  return amount * 100;
}

function countFutureFlightLegs(slices) {
  if (!Array.isArray(slices) || slices.length === 0) return 1;
  let count = 0;
  for (const slice of slices) {
    const departingAt = slice?.segments?.[0]?.departing_at;
    if (!departingAt || new Date(departingAt).getTime() > Date.now()) count += 1;
  }
  return count > 0 ? count : 1;
}

/**
 * Per-bag pricing for the post-booking baggage purchase flow.
 * @param {object} booking
 * @returns {{ unitPriceCents: number, unitPriceLabel: string, legCount: number, perWay: boolean } | { includedFree: true } | null}
 */
export function getCheckedBagPurchasePricing(booking) {
  const flightData = booking?.flightData || booking?.flight_data || {};
  const source = {
    slices: flightData.slices,
    bookingOption: flightData.bookingOption || flightData.booking_option,
    flightData,
  };

  const allowance = getBaggageAllowanceCounts(source, { isReturnLeg: false });
  if (!allowance) return null;

  if (allowance.checked > 0 && !allowance.checkedPrice) {
    return { includedFree: true };
  }

  const unitPriceCents = parseFormattedBaggagePriceToCents(allowance.checkedPrice);
  if (!unitPriceCents) return null;

  const legCount = countFutureFlightLegs(flightData.slices);
  return {
    unitPriceCents,
    unitPriceLabel: allowance.checkedPrice,
    legCount,
    perWay: legCount > 1,
  };
}

function getSliceCount(source) {
  const candidates = [
    source?.slices,
    source?.duffelOffer?.slices,
    source?.flightData?.slices,
    source?.flight_data?.slices,
  ].filter(Array.isArray);
  if (candidates.length === 0) return 0;
  return Math.max(...candidates.map((s) => s.length));
}

function isRoundTripBaggageSource(source, { isReturnLeg = false, roundTrip } = {}) {
  if (roundTrip === true) return true;
  if (roundTrip === false) return false;
  if (getSliceCount(source) > 1) return true;
  if (isReturnLeg) return true;

  const bookingOption =
    source?.bookingOption ||
    source?.booking_option ||
    source?.flightData?.bookingOption ||
    source?.flightData?.booking_option ||
    source?.flight_data?.bookingOption ||
    source?.flight_data?.booking_option;
  if (bookingOption?.returning) return true;

  return false;
}

/**
 * SerpAPI stores leg-specific baggage on departing/returning. Round-trip fares
 * often only have a trip-level `together` block — divide paid prices by 2 so
 * each leg shows the one-way fee (EBZIXV: $90 → $45 outbound + $45 return).
 */
function pickBookingOptionLeg(bookingOption, isReturnLeg) {
  if (!bookingOption || typeof bookingOption !== 'object') return null;

  if (isReturnLeg) {
    if (bookingOption.returning) {
      return { legOption: bookingOption.returning, priceScope: 'leg' };
    }
    if (bookingOption.together) {
      return { legOption: bookingOption.together, priceScope: 'trip' };
    }
    return null;
  }

  if (bookingOption.departing) {
    return { legOption: bookingOption.departing, priceScope: 'leg' };
  }
  if (bookingOption.together) {
    return { legOption: bookingOption.together, priceScope: 'trip' };
  }
  return null;
}

function collectBookingOptionBaggageStrings(legOption) {
  if (!legOption) return [];
  const extensions = (legOption.extensions || [])
    .map((ext) => (typeof ext === 'string' ? ext : ext?.text || ext?.value || ext?.label || ''))
    .filter(Boolean);
  const baggageExtensions = extensions.filter((ext) =>
    /carry[- ]?on|checked|personal item|baggage|luggage/i.test(ext),
  );
  return [...(legOption.baggage_prices || []), ...baggageExtensions];
}

/**
 * @param {object} source - Flight offer or enriched booking slice wrapper
 * @param {{ isReturnLeg?: boolean, roundTrip?: boolean }} [options]
 * @returns {{ checked: number, carryOn: number, checkedPrice: string | null, carryOnPrice: string | null, priceScope: 'leg' | 'trip' | null } | null}
 */
export function getBaggageAllowanceCounts(source, { isReturnLeg = false, roundTrip: roundTripOverride } = {}) {
  if (!source) return null;

  const slices =
    source.slices ||
    source.duffelOffer?.slices ||
    source.flightData?.slices ||
    source.flight_data?.slices;

  const fromDuffel = getBaggageAllowanceFromDuffelSlices(slices);
  if (fromDuffel) return fromDuffel;

  const bookingOption =
    source.bookingOption ||
    source.booking_option ||
    source.flightData?.bookingOption ||
    source.flightData?.booking_option ||
    source.flight_data?.bookingOption ||
    source.flight_data?.booking_option;

  const roundTrip = isRoundTripBaggageSource(source, { isReturnLeg, roundTrip: roundTripOverride });
  const picked = pickBookingOptionLeg(bookingOption, isReturnLeg);
  if (!picked) return null;

  const fromStrings = parseBaggageStrings(collectBookingOptionBaggageStrings(picked.legOption));
  if (!fromStrings) return null;

  const withScope = { ...fromStrings, priceScope: picked.priceScope };
  return applyTripLevelPerWayPricing(withScope, {
    priceScope: picked.priceScope,
    roundTrip,
  });
}

/**
 * Aggregate post-booking baggage purchases stored on flight_data.baggage_purchases.
 * @param {object | null | undefined} flightData
 * @returns {{ totalBags: number, baggageTotalCents: number, processingFeeCents: number, totalCents: number, purchaseCount: number } | null}
 */
export function getPurchasedBaggageSummary(flightData) {
  const purchases = flightData?.baggage_purchases;
  if (!Array.isArray(purchases) || purchases.length === 0) return null;

  let totalBags = 0;
  let baggageTotalCents = 0;
  let processingFeeCents = 0;
  let totalCents = 0;

  for (const purchase of purchases) {
    totalBags += (purchase.items || []).reduce(
      (sum, item) => sum + (Number(item.quantity) || 0),
      0,
    );
    baggageTotalCents += Number(purchase.baggage_total_cents) || 0;
    processingFeeCents += Number(purchase.processing_fee_cents) || 0;
    totalCents += Number(purchase.total_cents) || 0;
  }

  if (totalBags <= 0 && totalCents <= 0) return null;

  return {
    totalBags,
    baggageTotalCents,
    processingFeeCents,
    totalCents,
    purchaseCount: purchases.length,
  };
}

/**
 * After a post-booking baggage purchase, show checked-bag count (not price) on flight cards.
 * Purchased bags apply per flight leg — the count is the party total per leg.
 * @param {ReturnType<typeof getBaggageAllowanceCounts> | null} allowance
 * @param {object | null | undefined} flightData
 */
export function mergePurchasedBaggageIntoAllowance(allowance, flightData) {
  const summary = getPurchasedBaggageSummary(flightData);
  if (!summary || summary.totalBags <= 0) return allowance;

  const purchasedPerLeg = summary.totalBags;

  if (!allowance) {
    return {
      checked: purchasedPerLeg,
      carryOn: 0,
      checkedPrice: null,
      carryOnPrice: null,
      priceScope: null,
    };
  }

  return {
    ...allowance,
    checked: (allowance.checked ?? 0) + purchasedPerLeg,
    checkedPrice: null,
  };
}
